/**
 * 이상한 방 - 판이 끝났다 (결과 화면 + 라운드 결말 알림)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.5절(점수) · 8절(판정 지표)
 *            `이상한방-상태모델-v0.1.md` 11.3절(클라이언트가 해도 되는 것)
 *
 * `session.ts`에서 갈라져 나왔다. 라운드가 끝난 뒤에 답해야 하는 질문이 여기 모여 있고,
 * **셋이 서로 다른 질문이라는 것**이 이 파일이 한 덩어리인 이유다:
 *
 *  1. `show`      점수표에 무엇을 띄우는가 (단계 얼굴의 `cleared`는 단계 기준이다)
 *  2. `report`    진행 저장에 무엇을 넘기는가 (`cleared`는 **자유 모드에서 언제나 거짓**이다 -
 *                 저장할 단계가 없으므로)
 *  3. `metItsGoal` 끝소리를 무엇으로 고르는가 (자유 모드에는 기준이 없으므로 전량 발견이 목표다)
 *
 * 세 답이 미묘하게 갈리므로 흩어 두면 어느 것이 어느 질문의 답인지 알 수 없게 된다.
 * 실제로 세 곳이 각각 `meetsClearCondition`을 부르고 있었고, 그중 둘만 같은 뜻이었다.
 *
 * ## 판정식을 새로 쓰지 않는다
 *
 * 단계의 클리어 여부는 언제나 `levels.meetsClearCondition` **하나**에 묻는다. 여기서 비교식을
 * 한 줄이라도 쓰면 판정이 두 벌이 되고, 그때 나오는 증상은 "클리어라고 떴는데 다음 단계가
 * 안 열린다"이며 재현조차 어렵다. `metItsGoal`의 자유 모드 가지에 "절반 이상" 같은 문턱을
 * 지어내지 않는 것도 같은 이유다 - 새 문턱은 곧 새 규칙이고, 규칙은 리듀서에만 있어야 한다.
 *
 * ## 저장은 여기서 하지 않는다
 *
 * `onRoundEnd`로 바깥에 넘기고 끝이다. `localStorage`를 아는 순간 이 코드가 세션과 함께
 * 서버 권위로 옮겨 갈 때 같이 딸려 온다(11.3절).
 */

import {
  createResultScreen,
  type ResultLevelView,
  type ResultPinsView,
  type ResultScreenHandle,
  type ResultView,
} from '../ui/resultScreen'
import type { GameState } from './gameState'
import { computeScoreBreakdown } from './hunt'
import { meetsClearCondition, type LevelDef } from './levels'
import type { RevealStep } from './reveal'
import { missedFrom } from './sessionView'
import type { Tuning } from './tuning'

/**
 * 한 라운드의 결말. 진행 저장이 필요로 하는 것만 담는다.
 *
 * `elapsedMs`가 시계가 아니라 **프레임 누적 경과**인 것이 중요하다 -
 * `progress.ts`가 시계를 읽지 않기로 한 이유(탭 전환 시 벽시계와 플레이 시간이 갈린다)를
 * 그대로 잇는다.
 */
export type RoundOutcome = Readonly<{
  levelId: number | null
  cleared: boolean
  foundCount: number
  changeCount: number
  score: number
  elapsedMs: number
  /**
   * 무오답 전량 발견. 핀 산식(`economy/pins.ts`의 `SoloOutcome.perfect`)이 받는 값이고, 점수표의 퍼펙트 도장과
   * **같은 계산**(`computeScoreBreakdown().isPerfect`)에서 온다 - 여기서 비교식을 새로 쓰면 도장은 찍혔는데
   * 핀은 안 붙는 판이 생긴다.
   */
  perfect: boolean
}>

export type SessionResultOptions = Readonly<{
  /** 결과 화면을 붙일 DOM 컨테이너. 세션의 `mount`와 같은 곳이다. */
  mount: HTMLElement
  tuning: Tuning
  /** 이번 라운드가 속한 튜토리얼 단계. `null`이면 자유 모드다. */
  level: LevelDef | null
  /** 다음 단계가 존재하는가. 생략하면 없다. */
  hasNextLevel?: boolean | undefined
  onPlayAgain: () => void
  /** 없으면 결과 화면에 재도전 버튼이 없다(여럿이 - 다음 라운드는 `onPlayAgain` 하나다). 싱글은 언제나 넘긴다. */
  onRetrySameSeed?: (() => void) | undefined
  onNextLevel?: (() => void) | undefined
  onExitToSelect?: (() => void) | undefined
  /**
   * 라운드가 끝났다. **라운드당 정확히 한 번** 불린다 - 진행 저장이 `plays`를 세므로
   * 두 번 불리면 시도 횟수가 조용히 부풀어 오른다.
   */
  onRoundEnd?: ((outcome: RoundOutcome) => void) | undefined
  /**
   * 이 판의 핀 줄(로드맵 M3). **조회 함수**인 이유는 값이 `onRoundEnd`가 진행을 확정한 뒤에야 생기기 때문이다 -
   * `report`가 `show`보다 먼저 불리는 순서(`session.ts`의 `result` 페이즈)를 그대로 탄다. 세션은 핀을 계산하지
   * 않는다. 계산도 저장도 앱(`main.ts`)의 것이고, 여기는 그 결과를 결과 화면에 전달할 뿐이다.
   */
  resultPins?: (() => ResultPinsView | null) | undefined
}>

export type { ResultPinsView }

export type SessionResult = Readonly<{
  /**
   * 점수표를 띄운다. 여러 번 불려도 좋다 - 배치 모드에서 돌아올 때도, 치명적 오류에서도 뜬다.
   *
   * @param huntElapsedMs 수색에 실제로 쓴 시간(프레임 누적).
   * @param revealSteps   이번 라운드의 리빌 스텝. "놓친 변화" 목록이 여기서 나온다.
   */
  show: (state: GameState, huntElapsedMs: number, revealSteps: readonly RevealStep[]) => void
  /**
   * 이미 만들어진 뷰를 그대로 띄운다(여럿이, 로드맵 M2). 매치 세션은 `GameState`가 없고 `ClientView`를 받으므로
   * 점수표의 모양은 `matchSessionView.buildMatchResultView`가 만들고 여기는 화면에 넘길 뿐이다 - **규칙이 없다.**
   * `show`와 같은 화면 핸들을 지나므로 언어 전환·숨김·정리가 한 벌이다.
   */
  showView: (view: ResultView) => void
  hide: () => void
  /** 라운드 결말을 **한 번만** 바깥에 알린다. */
  report: (state: GameState, huntElapsedMs: number) => void
  /** 새 라운드가 섰다. 다음 결말은 다시 한 번 알려야 한다. */
  armReport: () => void
  /**
   * 이 라운드가 목표를 이뤘는가. **끝소리를 고르는 유일한 근거다**(`session.ts`의 `roundOverSound`).
   *
   * 두 모드의 목표가 다르므로 묻는 곳도 다르다 - 단계는 `levels.meetsClearCondition`이
   * 정하고(판정식은 언제나 그 함수 하나다), 자유 모드에는 기준이 없으므로 전량 발견이 목표다.
   */
  metItsGoal: (state: GameState) => boolean
  dispose: () => void
}>

export function createSessionResult(options: SessionResultOptions): SessionResult {
  const { tuning, level } = options

  /** 이번 라운드의 결말을 이미 알렸는가. `onRoundEnd`를 라운드당 한 번으로 묶는 잠금이다. */
  let reported = false

  const screen: ResultScreenHandle = createResultScreen({
    mount: options.mount,
    handlers: {
      onPlayAgain: options.onPlayAgain,
      /*
       * 두 모드의 재도전이 **같은 경로**다: 방은 그대로, 변화만 새로.
       *
       * 직전까지 이 자리는 `restart(state.seed)`였다. 단계는 시드가 고정이라 방도 변화도
       * 그대로 다시 나왔고, 리빌에서 정답 12개를 본 사람이 그 답을 그대로 다시 찍었다 -
       * 튜토리얼의 재도전이 학습이 아니라 답안 옮겨 적기가 된다.
       */
      ...(options.onRetrySameSeed === undefined ? {} : { onRetrySameSeed: options.onRetrySameSeed }),
      ...(options.onNextLevel === undefined ? {} : { onNextLevel: options.onNextLevel }),
      ...(options.onExitToSelect === undefined
        ? {}
        : { onExitToSelect: options.onExitToSelect }),
    },
  })

  /**
   * 결과 화면에 씌울 단계 얼굴. 자유 모드면 `undefined`.
   * 클리어 판정은 **부르기만** 한다 - 여기서 비교식을 쓰면 판정이 두 벌이 된다.
   */
  function levelResultView(state: GameState): ResultLevelView | undefined {
    if (level === null) return undefined

    return {
      order: level.id,
      name: level.name,
      cleared: meetsClearCondition(level, state.found.length),
      minFound: level.clearCondition.minFound,
      hasNext: options.hasNextLevel ?? false,
    }
  }

  return {
    show(state: GameState, huntElapsedMs: number, revealSteps: readonly RevealStep[]): void {
      const breakdown = computeScoreBreakdown(state, tuning)
      const levelView = levelResultView(state)
      const pinsView = options.resultPins?.() ?? null

      screen.show({
        seed: state.seed,
        // 시드만으로는 이 판이 재현되지 않는다 - 변화는 시도 번호에서도 나온다.
        attempt: state.attempt,
        foundCount: state.found.length,
        changeCount: state.changes.length,
        ammoLeft: state.ammo,
        huntElapsedMs,
        score: state.score,
        /*
         * 내역을 통째로 넘긴다. 골라서 넘기면 결과 화면이 보여 줄 수 있는 항목이 이 줄에
         * 묶이고, 무엇보다 **콤보 기여분이 빠진 표는 산수가 맞지 않는다**(`hunt.ScoreBreakdown`).
         * 최고 콤보만 상태에서 따로 읽지 않는 것도 같은 이유다 - 한 곳에서 온 값이어야
         * 총점과 내역이 같은 계산의 결과임이 보장된다.
         */
        breakdown: {
          findPoints: breakdown.findPoints,
          comboBonus: breakdown.comboBonus,
          raisedBonus: breakdown.raisedBonus,
          speedBonus: breakdown.speedBonus,
          perfectBonus: breakdown.perfectBonus,
          maxCombo: breakdown.maxCombo,
          isPerfect: breakdown.isPerfect,
        },
        endReason: state.huntEndReason ?? 'skipped',
        missed: missedFrom(revealSteps),
        ...(levelView === undefined ? {} : { level: levelView }),
        ...(pinsView === null ? {} : { pins: pinsView }),
      })
    },

    showView(view: ResultView): void {
      screen.show(view)
    },

    hide: () => {
      screen.hide()
    },

    /**
     * `show`에 붙이지 않은 이유: 결과 화면은 배치 모드에서 돌아올 때도 다시 그려지고
     * (`setActive`), 치명적 오류 경로에서도 뜬다. 진행 저장은 `plays`를 세므로
     * 그때마다 불리면 시도 횟수가 부풀고 최고 기록이 엉뚱한 판으로 갱신된다.
     */
    report(state: GameState, huntElapsedMs: number): void {
      if (reported || options.onRoundEnd === undefined) return
      reported = true

      options.onRoundEnd({
        levelId: level?.id ?? null,
        cleared: level !== null && meetsClearCondition(level, state.found.length),
        foundCount: state.found.length,
        changeCount: state.changes.length,
        score: state.score,
        elapsedMs: huntElapsedMs,
        perfect: computeScoreBreakdown(state, tuning).isPerfect,
      })
    },

    armReport(): void {
      reported = false
    },

    metItsGoal(state: GameState): boolean {
      if (level !== null) return meetsClearCondition(level, state.found.length)
      return state.changes.length > 0 && state.found.length >= state.changes.length
    },

    dispose(): void {
      screen.dispose()
    },
  }
}
